// redux/ai/aiSlice.js
import { createSlice } from "@reduxjs/toolkit";
import { sendAiMessage, fetchAiStatus, fetchAiRecent } from "./aiActions";

const initialState = {
  messages: [], // { id, role: 'user'|'assistant', content, provider?, model?, groundedOnData?, isError? }
  provider: "auto", // 'auto' | 'gpt' | 'gemini' (manual override)
  isOpen: false,
  status: "idle", // idle | loading | succeeded | failed
  error: null,
  providersLive: { gpt: false, gemini: false },
  recent: [], // recently asked questions (from tbl_chat_history)
  nextId: 1,
};

const aiSlice = createSlice({
  name: "ai",
  initialState,
  reducers: {
    toggleChat(state, action) {
      state.isOpen =
        typeof action.payload === "boolean" ? action.payload : !state.isOpen;
    },
    setProvider(state, action) {
      state.provider = action.payload;
    },
    addUserMessage(state, action) {
      state.messages.push({
        id: state.nextId++,
        role: "user",
        content: action.payload,
      });
    },
    // For "edit message": drop the given message and everything after it,
    // so the edited version can be re-sent and the reply regenerated.
    truncateFromMessage(state, action) {
      const idx = state.messages.findIndex((m) => m.id === action.payload);
      if (idx !== -1) state.messages = state.messages.slice(0, idx);
    },
    clearChat(state) {
      state.messages = [];
      state.error = null;
      state.status = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(sendAiMessage.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(sendAiMessage.fulfilled, (state, action) => {
        state.status = "succeeded";
        const d = action.payload || {};
        state.messages.push({
          id: state.nextId++,
          role: "assistant",
          content: d.reply || "(no response)",
          provider: d.provider,
          model: d.model,
          groundedOnData: d.groundedOnData,
          tasks: d.tasks || null,
          overview: d.overview || null,
          subject: d.subject || null,
          report: d.report || null,
          sql: d.sql || null,
        });
      })
      .addCase(sendAiMessage.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
        state.messages.push({
          id: state.nextId++,
          role: "assistant",
          content:
            typeof action.payload === "string"
              ? action.payload
              : "Something went wrong. Please try again.",
          isError: true,
        });
      })
      .addCase(fetchAiStatus.fulfilled, (state, action) => {
        state.providersLive = action.payload || state.providersLive;
      })
      .addCase(fetchAiRecent.fulfilled, (state, action) => {
        state.recent = Array.isArray(action.payload) ? action.payload : [];
      });
  },
});

export const {
  toggleChat,
  setProvider,
  addUserMessage,
  truncateFromMessage,
  clearChat,
} = aiSlice.actions;
export default aiSlice.reducer;
